import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

/** `?taskFlow=<flow>` shows a task's full-view flow (a trigger, notes, ...) over its workspace. */
export const TASK_FLOW_PARAM = 'taskFlow';
/** Marks the history entry our `open` pushed, so closing can pop it instead of stacking one. */
const PUSHED_STATE_KEY = 'taskFlowPushed';

function pushedByUs(state: unknown): boolean {
    return typeof state === 'object' && state !== null && PUSHED_STATE_KEY in state;
}

// Browser Back leaves the flow for the workspace. WorkQueue drops the param when the task changes,
// so a leftover never opens a flow on another task.
export function useTaskFlowParam<T extends string>(flows: readonly T[]) {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const raw = searchParams.get(TASK_FLOW_PARAM);
    const flow = flows.find((candidate) => candidate === raw) ?? null;

    const open = useCallback(
        (next: T) => {
            setSearchParams(
                (prev) => {
                    const params = new URLSearchParams(prev);
                    params.set(TASK_FLOW_PARAM, next);
                    return params;
                },
                { state: { [PUSHED_STATE_KEY]: true } }
            );
        },
        [setSearchParams]
    );

    const close = useCallback(() => {
        if (pushedByUs(location.state)) {
            navigate(-1);
            return;
        }
        // Arrived with the param already set (a reload or a shared link): drop it in place.
        setSearchParams(
            (prev) => {
                const params = new URLSearchParams(prev);
                params.delete(TASK_FLOW_PARAM);
                return params;
            },
            { replace: true }
        );
    }, [location.state, navigate, setSearchParams]);

    return { flow, open, close };
}
